/**
 * A cell as it travels on the clipboard (F-16, Kola-to-Kola range paste,
 * 2026-10): EVERYTHING the cell carries — value, format, translation status,
 * text marks and line kinds — in plain JSON, plus the one writer that lands it
 * on another cell so the copy is indistinguishable from its source.
 *
 * Lives in core/ (not beside the clipboard feature) because `BoardSession`
 * exposes the paste writer and core never imports from features/ (docs/08
 * §1). The clipboard carrier itself — what goes on the system clipboard and
 * how it is read back — is `features/board/cell-clipboard.ts`.
 *
 * Why not reuse `CellSnapshot` as-is: a snapshot is a READ view (it carries
 * `bg` twice — once flat, once inside `fmt`), and anything off the clipboard
 * is untrusted input that must be sanitized field by field before it touches
 * the doc. `CopiedCell` is the write-side shape, and `sanitizeCopiedCell` is
 * the only way one is made from foreign JSON.
 */
import {
  type BoardDoc,
  type CellContent,
  type CellFormat,
  type CellSnapshot,
  type CellStatus,
  type CellTextMarks,
  isBgmCellValue,
  isImageCellValue,
  sanitizeTextMarks,
  sanitizeTextTypeSwitches,
  TEXT_MARK_KINDS,
  type TextTypeSwitch,
} from "@kola/shared";

/** One copied cell. `v: null` is an empty cell that still has to CLEAR its target. */
export interface CopiedCell {
  v: CellContent;
  fmt?: CellFormat;
  st?: CellStatus;
  marks?: CellTextMarks;
  ty?: TextTypeSwitch[];
}

/** One cell to overwrite. `cell: null` = the source cell was absent/empty. */
export interface CellCopyWrite {
  rowId: string;
  colId: string;
  cell: CopiedCell | null;
}

/** The clipboard form of a live cell; null for a cell the doc does not hold. */
export function copiedCellOf(snapshot: CellSnapshot | undefined): CopiedCell | null {
  if (snapshot === undefined) return null;
  const cell: CopiedCell = { v: snapshot.v };
  if (snapshot.fmt !== undefined) cell.fmt = { ...snapshot.fmt };
  if (snapshot.st !== undefined) cell.st = snapshot.st;
  if (snapshot.marks !== undefined) {
    const marks: CellTextMarks = {};
    for (const kind of TEXT_MARK_KINDS) {
      const list = snapshot.marks[kind];
      if (list !== undefined && list.length > 0) marks[kind] = list.map((mark) => ({ ...mark }));
    }
    if (Object.keys(marks).length > 0) cell.marks = marks;
  }
  if (snapshot.ty !== undefined && snapshot.ty.length > 0) {
    cell.ty = snapshot.ty.map((sw) => ({ ...sw }));
  }
  return cell;
}

const ALIGNS: ReadonlySet<string> = new Set(["left", "center", "right"]);

/** Keep only the format properties with a value of the type the doc stores. */
function sanitizeFormat(raw: unknown): CellFormat | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const value = raw as Record<string, unknown>;
  const fmt: CellFormat = {};
  if (typeof value.bold === "boolean") fmt.bold = value.bold;
  if (typeof value.italic === "boolean") fmt.italic = value.italic;
  if (typeof value.underline === "boolean") fmt.underline = value.underline;
  if (typeof value.strike === "boolean") fmt.strike = value.strike;
  if (typeof value.color === "string") fmt.color = value.color;
  if (typeof value.bg === "string") fmt.bg = value.bg;
  if (typeof value.align === "string" && ALIGNS.has(value.align)) {
    fmt.align = value.align as CellFormat["align"];
  }
  return Object.keys(fmt).length > 0 ? fmt : undefined;
}

function isCellContent(value: unknown): value is CellContent {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    isImageCellValue(value) ||
    isBgmCellValue(value)
  );
}

/**
 * A copied cell from foreign JSON, or null for anything that is not one.
 * Lenient per FIELD: a field that fails its check is dropped (the cell still
 * pastes without it) — only a value the doc could not hold rejects the cell,
 * since a cell with no honest `v` has nothing to paste.
 */
export function sanitizeCopiedCell(raw: unknown): CopiedCell | null {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  const v = value.v === undefined ? null : value.v;
  if (!isCellContent(v)) return null;
  const cell: CopiedCell = { v };
  const fmt = sanitizeFormat(value.fmt);
  if (fmt !== undefined) cell.fmt = fmt;
  if (value.st === "mt" || value.st === "outdated") cell.st = value.st;
  if (typeof value.marks === "object" && value.marks !== null) {
    const marks: CellTextMarks = {};
    for (const kind of TEXT_MARK_KINDS) {
      const list = sanitizeTextMarks((value.marks as Record<string, unknown>)[kind]);
      if (list.length > 0) marks[kind] = list;
    }
    if (Object.keys(marks).length > 0) cell.marks = marks;
  }
  const ty = sanitizeTextTypeSwitches(value.ty);
  if (ty.length > 0) cell.ty = ty;
  return cell;
}

/**
 * Overwrite each target cell with its copy — ALL of it. A property the copy
 * lacks is CLEARED on the target (a plain cell pasted over a bold yellow one
 * lands plain, as it would in Sheets); a `null` copy empties the target.
 *
 * Order matters and mirrors `BoardSession.copyCell`: the value first (its own
 * rebase pass would otherwise re-anchor the marks against the target's OLD
 * text), then status, format, marks and line kinds — the last two as offsets
 * into text the target now carries verbatim.
 *
 * `st` is carried rather than cleared: a machine-translated cell copied
 * elsewhere is still machine-translated, and the badge should say so. (The
 * single-cell edit path clears it because a HUMAN typed the text; here the
 * human moved it.) One transaction per call = one sync unit = one undo step.
 */
export function applyCellCopies(
  board: BoardDoc,
  sheetId: string,
  writes: readonly CellCopyWrite[],
  origin?: unknown,
): void {
  if (writes.length === 0) return;
  board.transact(() => {
    for (const { rowId, colId, cell } of writes) {
      board.setCellValue(sheetId, rowId, colId, cell?.v ?? null, origin);
      board.setCellStatus(sheetId, rowId, colId, cell?.st, origin);
      board.setCellFormat(sheetId, rowId, colId, cell?.fmt, origin);
      for (const kind of TEXT_MARK_KINDS) {
        board.setCellMarks(sheetId, rowId, colId, kind, cell?.marks?.[kind] ?? [], origin);
      }
      board.setCellTextTypes(sheetId, rowId, colId, cell?.ty ?? [], origin);
    }
  }, origin);
}
